import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import {
  DarkMode,
  History,
  Insights,
  LightMode,
  Logout,
  Print,
  Receipt,
  Storefront,
} from '@mui/icons-material';
import {
  Button,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import { captureException } from '@sentry/react';
import { Formik } from 'formik';
import { ReactNode, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useShopsQuery } from '@frontend/data';
import { useLicenses } from '@frontend/hooks';
import { tApp, tAuth } from '@frontend/locales';
import { useThemeMode } from '@frontend/merchant-public-configuration';
import { routes, useMetadata, useUser } from '@frontend/utils';

import { SideDrawer } from '../side-drawer.component';
import { useToast } from '../toast.component';
import { AppDrawerShopSelectComponent } from './app-drawer-shop-select.component';

interface DrawerListItemButtonProps {
  eventId: string;
  isActive: boolean;
  onClick(): void;
  icon: ReactNode;
  title: string;
  disabled?: boolean;
}

const DrawerListItemButton = (props: DrawerListItemButtonProps) => {
  const theme = useTheme();

  return (
    <ListItemButton
      data-eventid={props.eventId}
      component="a"
      sx={{
        color: theme.palette.text.secondary,
        ...(props.isActive && {
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.neutral.light,
        }),
        py: 1,
        px: 2,
      }}
      onClick={props.onClick}
      disabled={props.disabled}
    >
      <ListItemIcon>{props.icon}</ListItemIcon>
      <ListItemText primary={props.title} />
    </ListItemButton>
  );
};

export interface ShopSelectInput {
  shopId: string;
}

export interface AppDrawerProps {
  openState: boolean;
  toggleDrawer(state: boolean): void;
}

export const AppDrawer = (props: AppDrawerProps) => {
  const { pathname } = useLocation();
  const { user, logout } = useUser();
  const navigate = useNavigate();
  const { appVersion } = useMetadata();
  const { shopId } = useParams();
  const { showToast } = useToast();
  const theme = useTheme();
  const { mode, setMode } = useThemeMode();

  const shops = useShopsQuery({
    shopIds: user?.shops ?? [],
    onError: (error) => {
      showToast({
        header: tApp.drawer.siteUnitsError(),
        body: tApp.errorPages.unexpectedErrorDescription(),
        severity: 'error',
      });
      captureException(error, {
        level: 'error',
        extra: {
          message: `Failed to fetch shops for user ${user.id} with shop IDs ${user.shops.join(', ')}`,
        },
      });
    },
  });

  useEffect(() => {
    if (props.openState && Capacitor.getPlatform() === 'android') {
      const listener = App.addListener('backButton', () => {
        props.toggleDrawer(false);
      });

      return () => {
        listener
          .then((handle) => handle.remove())
          .catch((error) => {
            console.error(error);
            captureException(error);
          });
      };
    }

    return undefined;
  }, [props.openState, props.toggleDrawer]);

  const isActive = (route: string) => {
    if (route === '/') {
      return pathname === route;
    }

    return pathname.includes(route);
  };

  const licenses = useLicenses();

  return (
    <SideDrawer openState={props.openState} toggleDrawer={props.toggleDrawer}>
      {shops && shops.data?.length > 1 && (
        <Formik<ShopSelectInput>
          initialValues={{
            shopId: shopId || user?.shops[0]?.toString() || '',
          }}
          onSubmit={() => {
            // Never submitted: the shop select acts on change.
          }}
        >
          <AppDrawerShopSelectComponent options={shops.data} />
        </Formik>
      )}

      <List component="nav">
        <DrawerListItemButton
          disabled={!user}
          eventId="app-drawer-orders"
          isActive={user !== undefined && isActive(routes.orders(shopId))}
          onClick={() => {
            if (isActive(routes.orders(shopId))) {
              props.toggleDrawer(false);
              return;
            }

            navigate(routes.orders(shopId));
          }}
          icon={
            <Receipt
              color={
                user !== undefined && isActive(routes.orders(shopId))
                  ? 'action'
                  : 'inherit'
              }
            />
          }
          title={tApp.headers.orders()}
        />

        <DrawerListItemButton
          disabled={!user}
          eventId="app-drawer-order-history"
          isActive={user !== undefined && isActive(routes.orderHistory(shopId))}
          onClick={() => {
            if (isActive(routes.orderHistory(shopId))) {
              props.toggleDrawer(false);
              return;
            }

            navigate(routes.orderHistory(shopId));
          }}
          icon={
            <History
              color={
                user !== undefined && isActive(routes.orderHistory(shopId))
                  ? 'action'
                  : 'inherit'
              }
            />
          }
          title={tApp.headers.orderHistory()}
        />

        <DrawerListItemButton
          disabled={!user}
          eventId="app-drawer-stats"
          isActive={user !== undefined && isActive(routes.stats(shopId))}
          onClick={() => {
            if (isActive(routes.stats(shopId))) {
              props.toggleDrawer(false);
              return;
            }

            navigate(routes.stats(shopId));
          }}
          icon={
            <Insights
              color={
                user !== undefined && isActive(routes.stats(shopId))
                  ? 'action'
                  : 'inherit'
              }
            />
          }
          title={tApp.headers.stats()}
        />

        {licenses.kitchenDisplay && (
          <DrawerListItemButton
            disabled={!user}
            eventId="app-drawer-kitchen-display"
            isActive={
              user !== undefined && isActive(routes.kitchenDisplay(shopId))
            }
            onClick={() => {
              if (isActive(routes.kitchenDisplay(shopId))) {
                props.toggleDrawer(false);
                return;
              }

              navigate(routes.kitchenDisplay(shopId));
            }}
            icon={
              <Receipt
                color={
                  user !== undefined && isActive(routes.kitchenDisplay(shopId))
                    ? 'action'
                    : 'inherit'
                }
              />
            }
            title={tApp.headers.kitchenDisplay()}
          />
        )}

        <DrawerListItemButton
          disabled={!user}
          eventId="app-drawer-printer-settings"
          isActive={
            user !== undefined && isActive(routes.printerSettings(shopId))
          }
          onClick={() => {
            if (isActive(routes.printerSettings(shopId))) {
              props.toggleDrawer(false);
              return;
            }

            navigate(routes.printerSettings(shopId));
          }}
          icon={
            <Print
              color={
                user !== undefined && isActive(routes.printerSettings(shopId))
                  ? 'action'
                  : 'inherit'
              }
            />
          }
          title={tApp.headers.printerSettings()}
        />

        <DrawerListItemButton
          disabled={!user}
          eventId="app-drawer-site-configuration"
          isActive={
            user !== undefined && isActive(routes.siteConfiguration(shopId))
          }
          onClick={() => {
            if (isActive(routes.siteConfiguration(shopId))) {
              props.toggleDrawer(false);
              return;
            }

            navigate(routes.siteConfiguration(shopId));
          }}
          icon={
            <Storefront
              color={
                user !== undefined && isActive(routes.siteConfiguration(shopId))
                  ? 'action'
                  : 'inherit'
              }
            />
          }
          title={tApp.headers.siteConfiguration()}
        />
      </List>

      <Stack spacing={2} p={2} mt="auto">
        <Stack spacing={2} direction="row" justifyContent="space-between">
          <Typography variant="body1" color="textSecondary">
            {tApp.app.version()}
          </Typography>

          <Typography align="right" variant="body1" color="textSecondary">
            {appVersion}
          </Typography>
        </Stack>

        {APP_CONFIG.gitSha && (
          <Stack spacing={2} direction="row" justifyContent="space-between">
            <Typography variant="body1" color="textSecondary">
              {tApp.app.softwareVersion()}
            </Typography>

            <Typography align="right" variant="body1" color="textSecondary">
              {APP_CONFIG.gitSha.slice(
                APP_CONFIG.gitSha.length - 6,
                APP_CONFIG.gitSha.length,
              )}
            </Typography>
          </Stack>
        )}

        <Stack spacing={1}>
          <Typography variant="body1" color="textSecondary">
            {tApp.theme.label()}
          </Typography>

          <ToggleButtonGroup
            fullWidth
            exclusive
            size="small"
            value={mode}
            onChange={(_event, nextMode: 'light' | 'dark' | null) => {
              if (nextMode) {
                setMode(nextMode);
              }
            }}
          >
            <ToggleButton
              data-eventid="app-drawer-theme-light"
              value="light"
              aria-label={tApp.theme.light()}
            >
              <LightMode fontSize="small" sx={{ mr: 1 }} />
              {tApp.theme.light()}
            </ToggleButton>
            <ToggleButton
              data-eventid="app-drawer-theme-dark"
              value="dark"
              aria-label={tApp.theme.dark()}
            >
              <DarkMode fontSize="small" sx={{ mr: 1 }} />
              {tApp.theme.dark()}
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        <Button
          fullWidth
          variant="outlined"
          startIcon={<Logout />}
          sx={{
            alignItems: 'center',
            borderColor: theme.palette.divider,
            color: theme.palette.text.secondary,
            '&:hover': {
              backgroundColor: theme.palette.neutral.light,
            },
          }}
          onClick={logout}
        >
          {tAuth.actions.logOut()}
        </Button>
      </Stack>
    </SideDrawer>
  );
};
